import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { initGoogleAnalytics } from './analytics';

// Multilingual support
const translations = {
  'pt-BR': {
    title: 'Conversor de Extrato Bancário para OFX',
    subtitle: 'Converta seus extratos bancários (TXT, CSV, XLS e XLSX) para o formato OFX.',
    uploadLabel: 'Enviar Arquivo',
    uploadHint: 'Clique ou arraste e solte o arquivo TXT, CSV, XLS ou XLSX',
    uploadMax: 'Máx 10MB. Não suporta arquivos protegidos por senha.',
    uploadInfo: 'O arquivo deve conter apenas as colunas e linhas das movimentações bancárias, sem títulos, totais ou linhas extras.',
    selectedFile: 'Arquivo selecionado:',
    separatorDetected: 'Separador detectado:',
    separatorNotDetected: 'Não foi possível detectar o separador automaticamente.',
    separatorCheck: 'Caso a leitura dos dados não esteja correta, verifique se o arquivo está salvo com um dos separadores de coluna suportados:',
    separatorList: 'Vírgula (,), Ponto e Vírgula (;), Tabulação (\t), Pipe (|), Espaço ( )',
    separatorSave: 'Por favor, salve seu arquivo utilizando um dos separadores de coluna suportados:',
    hasHeaders: 'Primeira linha contém cabeçalhos',
    preview: 'Pré-visualização das 5 primeiras linhas.',
    valueFormat: 'Formato de Valor',
    valueFormatSingle: 'Coluna única de valor',
    valueFormatSeparate: 'Colunas separadas de crédito/débito',
    dateColumn: 'Coluna de Data',
    descriptionColumn: 'Coluna de Descrição',
    amountColumn: 'Coluna de Valor',
    creditColumn: 'Coluna de Crédito',
    debitColumn: 'Coluna de Débito',
    balanceColumn: 'Coluna de Saldo (opcional)',
    select: 'Selecione...',
    clear: 'Limpar',
    generate: 'Gerar OFX',
    download: 'Baixar OFX',
    outputFile: 'Nome do arquivo de saída',
    previewOfx: 'Pré-visualização do OFX',
    footer1: 'Todos os direitos reservados.',
    footer2: 'Processamento no lado do cliente.',
    footer3: 'Este aplicativo é gratuito e foi criado para ajudar você a converter seus extratos bancários. Por favor, revise sempre o arquivo OFX gerado antes de utilizá-lo, pois não oferecemos garantias ou suporte para eventuais erros. Use à vontade!'
  },
  'en': {
    title: 'Bank Statement to OFX Converter',
    subtitle: 'Convert your bank statements (TXT, CSV, XLS, XLSX) to OFX format.',
    uploadLabel: 'Upload File',
    uploadHint: 'Click or drag and drop TXT, CSV, XLS or XLSX file',
    uploadMax: 'Max 10MB. Password-protected files are not supported.',
    uploadInfo: 'The file must contain only the columns and rows of bank transactions, without headers, totals or extra lines.',
    selectedFile: 'Selected file:',
    separatorDetected: 'Detected separator:',
    separatorNotDetected: 'Could not detect separator automatically.',
    separatorCheck: 'If the data is not read correctly, make sure your file is saved with one of the supported column separators:',
    separatorList: 'Comma (,), Semicolon (;), Tab (\t), Pipe (|), Space ( )',
    separatorSave: 'Please save your file using one of the supported column separators:',
    hasHeaders: 'First row contains headers',
    preview: 'Preview of the first 5 rows.',
    valueFormat: 'Value Format',
    valueFormatSingle: 'Single value column',
    valueFormatSeparate: 'Separate credit/debit columns',
    dateColumn: 'Date Column',
    descriptionColumn: 'Description Column',
    amountColumn: 'Amount Column',
    creditColumn: 'Credit Column',
    debitColumn: 'Debit Column',
    balanceColumn: 'Balance Column (optional)',
    select: 'Select...',
    clear: 'Clear',
    generate: 'Generate OFX',
    download: 'Download OFX',
    outputFile: 'Output file name',
    previewOfx: 'OFX Preview',
    footer1: 'All rights reserved.',
    footer2: 'Client-side processing.',
    footer3: 'This app is free and was created to help you convert your bank statements. Always review the generated OFX file before using it, as we do not offer guarantees or support for possible errors. Enjoy!'
  },
  'es': {
    title: 'Convertidor de Extractos Bancarios a OFX',
    subtitle: 'Convierte tus extractos bancarios (TXT, CSV, XLS, XLSX) al formato OFX.',
    uploadLabel: 'Subir Archivo',
    uploadHint: 'Haz clic o arrastra y suelta el archivo TXT, CSV, XLS o XLSX',
    uploadMax: 'Máx 10MB. No se admiten archivos protegidos por contraseña.',
    uploadInfo: 'El archivo debe contener solo las columnas y filas de los movimientos bancarios, sin títulos, totales ni líneas extra.',
    selectedFile: 'Arquivo seleccionado:',
    separatorDetected: 'Separador detectado:',
    separatorNotDetected: 'No se pudo detectar el separador automáticamente.',
    separatorCheck: 'Si los datos no se leen correctamente, asegúrate de que tu archivo esté guardado con uno de los separadores de columna admitidos:',
    separatorList: 'Coma (,), Punto y coma (;), Tabulación (\t), Barra vertical (|), Espacio ( )',
    separatorSave: 'Por favor, guarda tu archivo usando uno de los separadores de columna admitidos:',
    hasHeaders: 'La primera fila contiene encabezados',
    preview: 'Vista previa de las primeras 5 filas.',
    valueFormat: 'Formato de Valor',
    valueFormatSingle: 'Columna única de valor',
    valueFormatSeparate: 'Columnas separadas de crédito/débito',
    dateColumn: 'Columna de Fecha',
    descriptionColumn: 'Columna de Descripción',
    amountColumn: 'Columna de Valor',
    creditColumn: 'Coluna de Crédito',
    debitColumn: 'Coluna de Débito',
    balanceColumn: 'Coluna de Saldo (opcional)',
    select: 'Seleccionar...',
    clear: 'Limpiar',
    generate: 'Generar OFX',
    download: 'Descargar OFX',
    outputFile: 'Nombre del archivo de salida',
    previewOfx: 'Vista previa de OFX',
    footer1: 'Todos los derechos reservados.',
    footer2: 'Procesamiento del lado del cliente.',
    footer3: 'Esta aplicación es gratuita y fue creada para ayudarte a convertir tus extractos bancarios. Revisa siempre el archivo OFX generado antes de usarlo, ya que no ofrecemos garantías ni soporte por posibles errores. ¡Disfruta!'
  },
  'fr': {
    title: 'Convertisseur de Relevés Bancaires en OFX',
    subtitle: 'Convertissez vos relevés bancaires (TXT, CSV, XLS, XLSX) au format OFX.',
    uploadLabel: 'Télécharger le fichier',
    uploadHint: 'Cliquez ou faites glisser et déposez le fichier TXT, CSV, XLS ou XLSX',
    uploadMax: 'Max 10 Mo. Les fichiers protégés par mot de passe ne sont pas pris en charge.',
    uploadInfo: 'Le fichier doit contenir uniquement les colonnes et lignes des transactions bancaires, sans titres, totaux ou lignes supplémentaires.',
    selectedFile: 'Fichier sélectionné :',
    separatorDetected: 'Séparateur détecté :',
    separatorNotDetected: 'Impossible de détecter automatiquement le séparateur.',
    separatorCheck: 'Si les données ne sont pas lues correctement, assurez-vous que votre fichier est enregistré avec l’un des séparateurs de colonne pris en charge :',
    separatorList: 'Virgule (,), Point-virgule (;), Tabulation (\t), Barre verticale (|), Espace ( )',
    separatorSave: 'Veuillez enregistrer votre fichier en utilisant l’un des séparateurs de colonne pris en charge :',
    hasHeaders: 'La première ligne contient des en-têtes',
    preview: 'Aperçu des 5 premières lignes.',
    valueFormat: 'Format de valeur',
    valueFormatSingle: 'Colonne de valeur unique',
    valueFormatSeparate: 'Colonnes séparées crédit/débit',
    dateColumn: 'Colonne de date',
    descriptionColumn: 'Colonne de description',
    amountColumn: 'Colonne de montant',
    creditColumn: 'Colonne de crédit',
    debitColumn: 'Colonne de débit',
    balanceColumn: 'Colonne de solde (optionnel)',
    select: 'Sélectionner...',
    clear: 'Effacer',
    generate: 'Générer OFX',
    download: 'Télécharger OFX',
    outputFile: 'Nom du fichier de sortie',
    previewOfx: 'Aperçu OFX',
    footer1: 'Tous droits réservés.',
    footer2: 'Traitement côté client.',
    footer3: 'Cette application est gratuite et a été créée pour vous aider à convertir vos relevés bancaires. Veuillez toujours vérifier le fichier OFX généré avant de l’utiliser, car nous n’offrons aucune garantie ni assistance en cas d’erreurs. Profitez-en !'
  },
  'it': {
    title: 'Convertitore di Estratti Conto Bancari in OFX',
    subtitle: 'Converti i tuoi estratti conto bancari (TXT, CSV, XLS, XLSX) in formato OFX.',
    uploadLabel: 'Carica File',
    uploadHint: 'Clicca o trascina e rilascia il file TXT, CSV, XLS o XLSX',
    uploadMax: 'Max 10MB. I file protetti da password non sono supportati.',
    uploadInfo: 'Il file deve contenere solo le colonne e le righe delle transazioni bancarie, senza titoli, totali o righe extra.',
    selectedFile: 'File selezionato:',
    separatorDetected: 'Separatore rilevato:',
    separatorNotDetected: 'Impossibile rilevare automaticamente il separatore.',
    separatorCheck: 'Se i dati non vengono letti correttamente, assicurati che il file sia salvato con uno dei separatori di colonna supportati:',
    separatorList: 'Virgola (,), Punto e virgola (;), Tabulazione (\t), Pipe (|), Spazio ( )',
    separatorSave: 'Salva il file utilizzando uno dei separadores di colonna supportati:',
    hasHeaders: 'La prima riga contiene intestações',
    preview: 'Anteprima delle prime 5 righe.',
    valueFormat: 'Formato Valore',
    valueFormatSingle: 'Colonna valore singolo',
    valueFormatSeparate: 'Colonne separate credito/debito',
    dateColumn: 'Colonna Data',
    descriptionColumn: 'Colonna Descrizione',
    amountColumn: 'Colonna Importo',
    creditColumn: 'Colonna Credito',
    debitColumn: 'Colonna Debito',
    balanceColumn: 'Colonna Saldo (opzionale)',
    select: 'Seleziona...',
    clear: 'Pulisci',
    generate: 'Genera OFX',
    download: 'Scarica OFX',
    outputFile: 'Nome file di output',
    previewOfx: 'Anteprima OFX',
    footer1: 'Tutti i diritti riservati.',
    footer2: 'Elaborazione lato client.',
    footer3: 'Questa app è gratuita ed è stata creata per aiutarti a convertire i tuoi estratti conto bancari. Controlla sempre il file OFX generato prima di utilizzarlo, poiché non offriamo garanzie o supporto per eventuali errori. Buon utilizzo!'
  },
  'de': {
    title: 'Kontoauszug zu OFX Konverter',
    subtitle: 'Konvertieren Sie Ihre Kontoauszüge (TXT, CSV, XLS, XLSX) in das OFX-Format.',
    uploadLabel: 'Datei hochladen',
    uploadHint: 'Klicken oder ziehen Sie die TXT-, CSV-, XLS- oder XLSX-Datei hierher',
    uploadMax: 'Max. 10MB. Passwortgeschützte Dateien werden nicht unterstützt.',
    uploadInfo: 'Die Datei darf nur die Spalten und Zeilen der Banktransaktionen enthalten, ohne Überschriften, Summen oder zusätzliche Zeilen.',
    selectedFile: 'Ausgewählte Datei:',
    separatorDetected: 'Erkannter Trenner:',
    separatorNotDetected: 'Trenner konnte nicht automatisch erkannt werden.',
    separatorCheck: 'Wenn die Daten nicht korrekt gelesen werden, stellen Sie sicher, dass Ihre Datei mit einem der unterstützten Spaltentrenner gespeichert ist:',
    separatorList: 'Komma (,), Semikolon (;), Tabulator (\t), Pipe (|), Leerzeichen ( )',
    separatorSave: 'Bitte speichern Sie Ihre Datei mit einem der unterstützten Spaltentrenner:',
    hasHeaders: 'Die erste Zeile enthält Überschriften',
    preview: 'Vorschau der ersten 5 Zeilen.',
    valueFormat: 'Wertformat',
    valueFormatSingle: 'Einzelne Wertspalte',
    valueFormatSeparate: 'Getrennte Spalten für Gutschrift/Lastschrift',
    dateColumn: 'Datumsspalte',
    descriptionColumn: 'Beschreibungsspalte',
    amountColumn: 'Betragsspalte',
    creditColumn: 'Gutschriftspalte',
    debitColumn: 'Lastschriftspalte',
    balanceColumn: 'Saldo-Spalte (optional)',
    select: 'Auswählen...',
    clear: 'Zurücksetzen',
    generate: 'OFX generieren',
    download: 'OFX herunterladen',
    outputFile: 'Name der Ausgabedatei',
    previewOfx: 'OFX-Vorschau',
    footer1: 'Alle Rechte vorbehalten.',
    footer2: 'Verarbeitung auf der Client-Seite.',
    footer3: 'Diese App ist kostenlos und wurde entwickelt, um Ihnen bei der Konvertierung Ihrer Kontoauszüge zu helfen. Bitte überprüfen Sie die generierte OFX-Datei immer vor der Verwendung, da wir keine Garantien oder Support für eventuelle Fehler bieten. Viel Spaß!'
  },
};

function App() {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({
    date: '',
    amount: '',
    creditColumn: '',
    debitColumn: '',
    description: '',
    type: '',
    balance: '',
  });
  const [ofxContent, setOfxContent] = useState('');
  const [message, setMessage] = useState('');
  const [fileInfoMessage, setFileInfoMessage] = useState('');
  const [valueFormat, setValueFormat] = useState('single');
  const [outputFileName, setOutputFileName] = useState('extrato.ofx');
  const [errors, setErrors] = useState({
    date: false,
    amount: false,
    creditColumn: false,
    debitColumn: false,
    description: false,
  });
  const [csvSeparator, setCsvSeparator] = useState(';');
  const [customSeparator, setCustomSeparator] = useState('');
  const [detectedSeparator, setDetectedSeparator] = useState('');
  const [appliedSeparator, setAppliedSeparator] = useState('');
  const [showSeparatorSelector, setShowSeparatorSelector] = useState(false);
  const [hasHeaders, setHasHeaders] = useState(false);
  const [startDataLine, setStartDataLine] = useState(0);
  const fileInputRef = useRef(null);

  // Informações bancárias padrão para o OFX
  const defaultBankInfo = {
    bankId: '999', // ID genérico
    bankName: 'Generic Bank', // Nome genérico
    accountId: '000000000',
    accountType: 'CHECKING',
    currency: 'BRL'
  };

  // Função para formatar a data no padrão OFX (YYYYMMDD)
  const formatOfxDate = (dateStr) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // Função para determinar o tipo de transação com base no valor
  const getTransactionType = (amount) => {
    if (amount > 0) return 'CREDIT';
    if (amount < 0) return 'DEBIT';
    return 'OTHER';
  };

  // Função para formatar tamanho do arquivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// Lida com a seleção de arquivos
const handleFileChange = (e) => {
    setFile(null);
    setParsedData([]);
    setHeaders([]);
    setMapping({
      date: '',
      amount: '',
      creditColumn: '',
      debitColumn: '',
      description: '',
      type: '',
      balance: '',
    });
    setOfxContent('');
    setMessage('');
    setFileInfoMessage('');

    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Verifica o tamanho do arquivo (máximo 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB em bytes
    if (selectedFile.size > MAX_FILE_SIZE) {
      const formattedSize = formatFileSize(selectedFile.size);
      const formattedMaxSize = formatFileSize(MAX_FILE_SIZE);
      setFileInfoMessage(`O arquivo tem ${formattedSize}. O tamanho máximo permitido é ${formattedMaxSize}.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setFile(selectedFile);

    // Atualiza o nome do arquivo de saída com base no nome do arquivo carregado
    const fileNameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
    setOutputFileName(`${fileNameWithoutExt}.ofx`);

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExtension === 'xls' || fileExtension === 'xlsx') {
      // Arquivos Excel
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        let fileHeaders;
        let dataLines;
        if (hasHeaders) {
          fileHeaders = jsonData[startDataLine].map(h => h || 'Coluna');
          dataLines = jsonData.slice(startDataLine + 1);
        } else {
          fileHeaders = Array.from({ length: jsonData[startDataLine].length }, (_, i) => `Coluna ${i + 1}`);
          dataLines = jsonData.slice(startDataLine);
        }
        
        setHeaders(fileHeaders);
        const parsedRows = dataLines.map((row) => {
          const rowObject = {};
          fileHeaders.forEach((header, index) => {
            rowObject[header] = row[index];
          });
          return rowObject;
        });
        setFileInfoMessage('');
        setParsedData(parsedRows);
      };
      reader.readAsArrayBuffer(selectedFile);
      return;
    }

    // Handle TXT/CSV files
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;

      // Check for RTF or binary content
      const isRTF = data.trim().startsWith('{\\rtf');
      const isLikelyBinary = /[\x00-\x08\x0E-\x1F]/.test(data.slice(0, 100));
      if (isRTF || isLikelyBinary) {
        setFileInfoMessage(isRTF ? 
          'Arquivo RTF detectado. Por favor, envie apenas arquivos de texto plano (.txt, .csv, .xls, .xlsx) exportados corretamente.' :
          'Arquivo binário ou formato não suportado detectado. Por favor, envie apenas arquivos de texto plano (.txt, .csv, .xls, .xlsx).');
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Detect separator but don't apply immediately
      const detected = detectSeparator(data);
      setDetectedSeparator(detected.label);
      setCsvSeparator(detected.sep);
      // Usa o valor atual de hasHeaders ao processar
      parseTextFile(data, selectedFile, detected.sep, false, hasHeaders);
    };
    reader.readAsText(selectedFile);
  };

  // Função para detectar o separador mais provável em arquivos TXT/CSV
  const detectSeparator = (data) => {
    const candidates = [
      { sep: ';', label: 'Ponto e vírgula (;)' },
      { sep: ',', label: 'Vírgula (,)' },
      { sep: '\t', label: 'Tab' },
      { sep: '|', label: 'Pipe (|)' },
      { sep: ' ', label: 'Espaço' }
    ];
    const lines = data.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 0) return { sep: ';', label: 'Ponto e vírgula (;)' };
    const sample = lines.slice(0, 5);
    let best = { sep: ';', label: 'Ponto e vírgula (;)' }, maxCount = 0;
    candidates.forEach(c => {
      const count = sample.map(line => (c.sep === '\t' ? line.split(/\t/).length : line.split(c.sep).length)).reduce((a, b) => a + b, 0);
      if (count > maxCount) {
        maxCount = count;
        best = c;
      }
    });
    return best;
  };

  // Estado para guardar o último separador realmente aplicado e valores iniciais
  const [lastAppliedSeparator, setLastAppliedSeparator] = useState('');
  const [lastAppliedSeparatorLabel, setLastAppliedSeparatorLabel] = useState('');

  const formatSeparatorLabel = (sep) => {
    if (sep === '\t' || sep === '\\t' || sep === '\u0009' || sep === String.fromCharCode(9)) {
      return 'Tab';
    } else if (sep === ' ') {
      return 'Espaço';
    } else if (sep === ';') {
      return 'Ponto e vírgula (;)';
    } else if (sep === ',') {
      return 'Vírgula (,)';
    } else if (sep === '|') {
      return 'Pipe (|)';
    }
    return sep;
  };

  // Atualiza parseTextFile para aceitar hasHeaders explícito
  const parseTextFile = (data, selectedFile, fixedDelimiter, isApply = false, forcedHasHeaders = null) => {
    // Usa o valor de hasHeaders explicitamente se fornecido
    const useHasHeaders = forcedHasHeaders !== null ? forcedHasHeaders : hasHeaders;
    let delimiter = isApply ? fixedDelimiter : (lastAppliedSeparator || fixedDelimiter);
    let realDelimiter = delimiter;
    if (realDelimiter === '\t' || realDelimiter === '\\t' || realDelimiter === '\u0009' || realDelimiter === String.fromCharCode(9)) {
      realDelimiter = '\t';
    }
    let separatorLabel = formatSeparatorLabel(delimiter);
    const lines = data.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 0) {
      setFileInfoMessage('O arquivo está vazio ou não pôde ser lido.');
      return;
    }
    let fileHeaders = [];
    let dataLines = [];
    if (useHasHeaders) {
      fileHeaders = realDelimiter === '\t'
        ? lines[startDataLine].split(/\t/).map(h => h.trim() || 'Coluna')
        : lines[startDataLine].split(realDelimiter).map(h => h.trim() || 'Coluna');
      dataLines = lines.slice(startDataLine + 1);
    } else {
      const firstLine = realDelimiter === '\t'
        ? lines[startDataLine].split(/\t/)
        : lines[startDataLine].split(realDelimiter);
      fileHeaders = Array.from({ length: firstLine.length }, (_, i) => `Coluna ${i + 1}`);
      dataLines = lines.slice(startDataLine);
    }
    if (isApply) {
      setLastAppliedSeparator(delimiter);
      setLastAppliedSeparatorLabel(separatorLabel);
      setAppliedSeparator(separatorLabel);
      setDetectedSeparator('');
      setShowSeparatorSelector(false);
    } else if (lastAppliedSeparator) {
      setAppliedSeparator(lastAppliedSeparatorLabel);
    } else {
      setDetectedSeparator(separatorLabel);
      setAppliedSeparator('');
    }
    const parsedRows = dataLines.map(line => {
      let values = realDelimiter === '\t'
        ? line.split(/\t/).map(v => v.trim())
        : line.split(realDelimiter).map(v => v.trim());
      if (values.length < fileHeaders.length) {
        values = values.concat(Array(fileHeaders.length - values.length).fill(''));
      } else if (values.length > fileHeaders.length) {
        values = values.slice(0, fileHeaders.length);
      }
      const rowObject = {};
      fileHeaders.forEach((header, index) => {
        rowObject[header] = values[index];
      });
      return rowObject;
    });
    setHeaders(fileHeaders);
    setParsedData(parsedRows);
    setFileInfoMessage('');
  };

  const [validationWarnings, setValidationWarnings] = useState([]); // Novos avisos de validação

  const handleMappingChange = (e) => {
    const { name, value } = e.target;
    setMapping(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: false }));
    setMessage('');
    // Removido setTimeout/validateMapping daqui
  };

  // Validação automática ao mudar mapping, parsedData ou valueFormat
  useEffect(() => {
    validateMapping();
    // eslint-disable-next-line
  }, [mapping, parsedData, valueFormat]);

  const handleOutputFileNameChange = (e) => {
    let newName = e.target.value;
    // Garante que o nome do arquivo termine com .ofx
    if (!newName.toLowerCase().endsWith('.ofx')) {
      newName += '.ofx';
    }
    setOutputFileName(newName);
  };

  const handleValueFormatChange = (e) => {
    setValueFormat(e.target.value);
    setMapping(prev => ({
      ...prev,
      amount: '',
      creditColumn: '',
      debitColumn: ''
    }));
    setErrors(prev => ({ // Limpar erros de valor ao mudar o formato
      ...prev,
      amount: false,
      creditColumn: false,
      debitColumn: false,
    }));
    setMessage(''); // Limpa a mensagem de erro ao mudar o formato
  };

  // Corrige handleHasHeadersChange para reprocessar o arquivo do zero, respeitando o valor atualizado de hasHeaders
  const handleHasHeadersChange = (e) => {
    const checked = e.target.checked;
    setHasHeaders(checked);
    setStartDataLine(0);
    setParsedData([]);
    setHeaders([]);
    setMapping({
      date: '',
      amount: '',
      creditColumn: '',
      debitColumn: '',
      description: '',
      type: '',
      balance: '',
    });
    setOfxContent('');
    setMessage('');
    setFileInfoMessage('');

    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const data = new Uint8Array(ev.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          let fileHeaders;
          let dataLines;
          if (checked) {
            fileHeaders = jsonData[startDataLine].map(h => h || 'Coluna');
            dataLines = jsonData.slice(startDataLine + 1);
          } else {
            fileHeaders = Array.from({ length: jsonData[startDataLine].length }, (_, i) => `Coluna ${i + 1}`);
            dataLines = jsonData.slice(startDataLine);
          }
          setHeaders(fileHeaders);
          const parsedRows = dataLines.map((row) => {
            const rowObject = {};
            fileHeaders.forEach((header, index) => {
              rowObject[header] = row[index];
            });
            return rowObject;
          });
          setFileInfoMessage('');
          setParsedData(parsedRows);
        };
        reader.readAsArrayBuffer(file);
      } else if (fileName.endsWith('.txt') || fileName.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const sep = lastAppliedSeparator || (csvSeparator === 'custom' ? customSeparator : csvSeparator);
          const sepLabel = lastAppliedSeparatorLabel || formatSeparatorLabel(sep);
          setAppliedSeparator(sepLabel);
          // Passa explicitamente o valor atualizado de checked para parseTextFile
          parseTextFile(ev.target.result, file, sep, Boolean(lastAppliedSeparator), checked);
        };
        reader.readAsText(file);
      }
    }
  };

  const isDateValid = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return true;
    // Tenta formatos comuns brasileiros
    const parts = dateStr.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
    if (parts) {
      let year = parseInt(parts[3], 10);
      if (year < 100) year += 2000;
      const month = parseInt(parts[2], 10) - 1;
      const day = parseInt(parts[1], 10);
      const newDate = new Date(year, month, day);
      return !isNaN(newDate.getTime());
    }
    return false;
  };
  const isNumberValid = (val) => {
    if (val === undefined || val === null) return false;
    const n = parseFloat(val.toString().replace(',', '.'));
    return !isNaN(n);
  };
  const hasDuplicateMapping = () => {
    const used = [];
    if (mapping.date) used.push(mapping.date);
    if (mapping.description) used.push(mapping.description);
    if (valueFormat === 'single' && mapping.amount) used.push(mapping.amount);
    if (valueFormat === 'separate') {
      if (mapping.creditColumn) used.push(mapping.creditColumn);
      if (mapping.debitColumn) used.push(mapping.debitColumn);
    }
    // Remove vazios e verifica duplicidade
    const filtered = used.filter(Boolean);
    return new Set(filtered).size !== filtered.length;
  };

  const validateMapping = () => {
    const warnings = [];
    // 1. Checa duplicidade
    if (hasDuplicateMapping()) {
      warnings.push('Não é permitido selecionar a mesma coluna para mais de um campo obrigatório.');
    }
    // 2. Valida datas (primeiras 5 linhas)
    if (mapping.date) {
      const invalidDates = parsedData.slice(0, 5).filter(row => !isDateValid(row[mapping.date]));
      if (invalidDates.length > 0) {
        warnings.push('A coluna de Data contém valores que não parecem datas válidas nas primeiras linhas.');
      }
    }
    // 3. Valida valores numéricos
    if (valueFormat === 'single') {
      if (mapping.amount) {
        const invalidVals = parsedData.slice(0, 5).filter(row => !isNumberValid(row[mapping.amount]));
        if (invalidVals.length > 0) {
          warnings.push('A coluna de Valor contém valores não numéricos nas primeiras linhas.');
        }
      }
    } else if (valueFormat === 'separate') {
      if (mapping.creditColumn) {
        const invalidCred = parsedData.slice(0, 5).filter((row) => !isNumberValid(row[mapping.creditColumn]));
        if (invalidCred.length > 0) {
          warnings.push('A coluna de Crédito contém valores não numéricos nas primeiras linhas.');
        }
      }
      if (mapping.debitColumn) {
        const invalidDeb = parsedData.slice(0, 5).filter((row) => !isNumberValid(row[mapping.debitColumn]));
        if (invalidDeb.length > 0) {
          warnings.push('A coluna de Débito contém valores não numéricos nas primeiras linhas.');
        }
      }
    }
    // 4. Saldo (opcional)
    if (mapping.balance) {
      const invalidBal = parsedData.slice(0, 5).filter((row) => !isNumberValid(row[mapping.balance]));
      if (invalidBal.length > 0) {
        warnings.push('A coluna de Saldo contém valores não numéricos nas primeiras linhas.');
      }
    }
    setValidationWarnings(warnings);
    return warnings.length === 0;
  };

  // Função para identificar células inválidas para destaque visual
  const getInvalidCells = () => {
    const invalid = { date: new Set(), amount: new Set(), creditColumn: new Set(), debitColumn: new Set(), balance: new Set() };
    // Datas inválidas
    if (mapping.date) {
      parsedData.slice(0, 5).forEach((row, idx) => {
        if (!isDateValid(row[mapping.date])) invalid.date.add(idx);
      });
    }
    // Valor único inválido
    if (valueFormat === 'single' && mapping.amount) {
      parsedData.slice(0, 5).forEach((row, idx) => {
        if (!isNumberValid(row[mapping.amount])) invalid.amount.add(idx);
      });
    }
    // Crédito/Débito inválidos
    if (valueFormat === 'separate') {
      if (mapping.creditColumn) {
        parsedData.slice(0, 5).forEach((row, idx) => {
          if (!isNumberValid(row[mapping.creditColumn])) invalid.creditColumn.add(idx);
        });
      }
      if (mapping.debitColumn) {
        parsedData.slice(0, 5).forEach((row, idx) => {
          if (!isNumberValid(row[mapping.debitColumn])) invalid.debitColumn.add(idx);
        });
      }
    }
    // Saldo inválido
    if (mapping.balance) {
      parsedData.slice(0, 5).forEach((row, idx) => {
        if (!isNumberValid(row[mapping.balance])) invalid.balance.add(idx);
      });
    }
    return invalid;
  };

  // Função para aplicar o separador selecionado e reprocessar o arquivo
  const handleApplySeparator = () => {
    if (csvSeparator === 'custom' && !customSeparator) {
      setMessage('Digite um separador personalizado antes de aplicar.');
      return;
    }
    if (file) {
      const sep = csvSeparator === 'custom' ? customSeparator : csvSeparator;
      const sepLabel = formatSeparatorLabel(sep);
      
      // Guarda o separador e label escolhidos e aplica imediatamente
      setLastAppliedSeparator(sep);
      setLastAppliedSeparatorLabel(sepLabel);
      setAppliedSeparator(sepLabel);
      setDetectedSeparator('');
      setShowSeparatorSelector(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        parseTextFile(e.target.result, file, sep, true);
      };
      reader.readAsText(file);
    }
  };

  const generateOFX = () => {
    setMessage('');
    if (!validateMapping()) {
      setMessage('Corrija os avisos de validação antes de gerar o OFX.');
      return;
    }
    let hasError = false;
    const newErrors = {
      date: false,
      amount: false,
      creditColumn: false,
      debitColumn: false,
      description: false,
    };

    if (parsedData.length === 0) {
      setMessage('Por favor, carregue um arquivo antes de gerar o OFX.');
      return;
    }

    if (!mapping.date) {
      newErrors.date = true;
      hasError = true;
    }
    if (!mapping.description) {
      newErrors.description = true;
      hasError = true;
    }

    if (valueFormat === 'single') {
      if (!mapping.amount) {
        newErrors.amount = true;
        hasError = true;
      }
    } else { // valueFormat === 'separate'
      if (!mapping.creditColumn) {
        newErrors.creditColumn = true;
        hasError = true;
      }
      if (!mapping.debitColumn) {
        newErrors.debitColumn = true;
        hasError = true;
      }
    }

    setErrors(newErrors);

    if (hasError) {
      setMessage('Por favor, selecione todas as colunas obrigatórias marcadas em vermelho.');
      setOfxContent('');
      return;
    }
    
    let transactions = '';
    let latestBalance = 0;
    let latestBalanceDate = '';

    parsedData.forEach((row, index) => {
      const date = formatOfxDate(row[mapping.date]);
      let amount = 0;

      if (valueFormat === 'single') {
        amount = parseFloat(row[mapping.amount]?.toString().replace(',', '.') || 0);
      } else { // valueFormat === 'separate'
        const creditValue = parseFloat(row[mapping.creditColumn]?.toString().replace(',', '.') || 0);
        const debitValue = parseFloat(row[mapping.debitColumn]?.toString().replace(',', '.') || 0);
        amount = creditValue - debitValue;
      }
      
      const description = row[mapping.description] || 'N/A';
      const payee = description; 
      const transactionType = getTransactionType(amount);
      const fitId = `TRN${index + 1}${date}${Math.abs(amount).toFixed(2).replace('.', '')}`;

      if (mapping.balance && row[mapping.balance] !== undefined) {
        latestBalance = parseFloat(row[mapping.balance]?.toString().replace(',', '.') || 0);
        latestBalanceDate = date.substring(0, 8); // MMDD
      }

      if (date && !isNaN(amount)) {
        transactions += `
          <STMTTRN>
            <TRNTYPE>${transactionType}</TRNTYPE>
            <DTPOSTED>${date.substring(0, 8)}</DTPOSTED>
            <TRNAMT>${amount.toFixed(2)}</TRNAMT>
            <FITID>${fitId}</FITID>
            <NAME>${payee}</NAME>
            <MEMO>${description}</MEMO>
          </STMTTRN>
        `;
      }
    });

    const dtClient = new Date().toISOString().slice(0, 19).replace(/[^0-9]/g, '');
    const dtStart = parsedData.length > 0 ? formatOfxDate(parsedData[0][mapping.date]).substring(0, 8) : dtClient.substring(0, 8);
    const dtEnd = parsedData.length > 0 ? formatOfxDate(parsedData[parsedData.length - 1][mapping.date]).substring(0, 8) : dtClient.substring(0, 8);

    const ofx = `OFXHEADER:100
DATA:OFXSGML
VERSION:102
SECURITY:NONE
ENCODING:USASCII
CHARSET:1252
COMPRESSION:NONE
OLDFILEUID:NONE
NEWFILEUID:NONE

<OFX>
  <SIGNONMSGSRQV1>
    <SONRQ>
      <DTCLIENT>${dtClient}</DTCLIENT>
      <USERID>OFXUSER</USERID>
      <USERPASS>OFXPASS</USERPASS>
      <LANGUAGE>POR</LANGUAGE>
      <FI>
        <ORG>${defaultBankInfo.bankName}</ORG>
        <FID>${defaultBankInfo.bankId}</FID>
      </FI>
      <APPID>OFXConverter</APPID>
      <APPVER>100</APPVER>
    </SONRQ>
  </SIGNONMSGSRQV1>

  <BANKMSGSRQV1>
    <STMTTRNRS>
      <TRNUID>1</TRNUID>
      <STATUS>
        <CODE>0</CODE>
        <SEVERITY>INFO</SEVERITY>
      </STATUS>
      <STMTRS>
        <CURDEF>${defaultBankInfo.currency}</CURDEF>
        <BANKACCTFROM>
          <BANKID>${defaultBankInfo.bankId}</BANKID>
          <ACCTID>${defaultBankInfo.accountId}</ACCTID>
          <ACCTTYPE>${defaultBankInfo.accountType}</ACCTTYPE>
        </BANKACCTFROM>
        <BANKTRANLIST>
          <DTSTART>${dtStart}</DTSTART>
          <DTEND>${dtEnd}</DTEND>
          ${transactions}
        </BANKTRANLIST>
        ${latestBalanceDate ? `
        <LEDGERBAL>
          <BALAMT>${latestBalance.toFixed(2)}</BALAMT>
          <DTASOF>${latestBalanceDate}</DTASOF>
        </LEDGERBAL>
        ` : ''}
      </STMTRS>
    </STMTTRNRS>
  </BANKMSGSRQV1>
</OFX>
    `;
    setOfxContent(ofx.trim());
    setMessage('Arquivo OFX gerado com sucesso!'); // Mensagem de sucesso na geração
  };

  const downloadOFX = () => {
    if (!ofxContent) {
      setMessage('Nenhum conteúdo OFX para baixar. Gere o OFX primeiro.');
      return;
    }
    const blob = new Blob([ofxContent], { type: 'application/ofx' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = outputFileName; // Usa o nome de arquivo definido pelo usuário
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Resetar o estado da aplicação para o estado inicial
    setFile(null);
    setParsedData([]);
    setHeaders([]);
    setMapping({
      date: '',
      amount: '',
      creditColumn: '',
      debitColumn: '',
      description: '',
      type: '',
      balance: '',
    });
    setOfxContent('');
    setMessage(''); // Limpa a mensagem de validação/geração
    setFileInfoMessage(''); // Limpa a mensagem de informação do arquivo
    setValueFormat('single');
    setOutputFileName('extrato.ofx'); // Resetar nome do arquivo de saída
    setErrors({ // Resetar erros
      date: false,
      amount: false,
      creditColumn: false,
      debitColumn: false,
      description: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Limpa o valor do input de arquivo nativo
    }
  };

  // Função utilitária para filtrar colunas disponíveis para cada campo obrigatório
  const getAvailableHeaders = (field) => {
    // Lista de campos obrigatórios que não podem se repetir
    const fields = valueFormat === 'single'
      ? ['date', 'description', 'amount']
      : ['date', 'description', 'creditColumn', 'debitColumn'];
    // Colunas já selecionadas em outros campos obrigatórios
    const used = fields.filter(f => f !== field).map(f => mapping[f]).filter(Boolean);
    // Sempre mostrar a coluna atualmente selecionada para este campo
    return headers.filter(h => !used.includes(h) || mapping[field] === h);
  };

  // Função para checar se todos os campos obrigatórios estão preenchidos
  const isMappingComplete = () => {
    if (!mapping.date || !mapping.description) return false;
    if (valueFormat === 'single') {
      if (!mapping.amount) return false;
    } else {
      if (!mapping.creditColumn || !mapping.debitColumn) return false;
    }
    return true;
  };

  // Tooltip customizado para o botão Trocar separador
  const [showSeparatorTooltip, setShowSeparatorTooltip] = useState(false);

  // Adiciona handlers para drag-and-drop
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const dropArea = document.getElementById('file-drop-area');
    if (dropArea) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, preventDefaults, false);
      });
      dropArea.addEventListener('drop', (e) => {
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          const fakeEvent = { target: { files: e.dataTransfer.files } };
          handleFileChange(fakeEvent);
        }
      }, false);
    }
    return () => {
      if (dropArea) {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
          dropArea.removeEventListener(eventName, preventDefaults, false);
        });
      }
    };
  }, []);

  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  // Novo helper para saber se o arquivo é TXT ou CSV
  const isTxtOrCsv = file => {
    if (!file) return false;
    const name = file.name.toLowerCase();
    return name.endsWith('.txt') || name.endsWith('.csv');
  };

  // 1. Detecta idioma do navegador e seleciona tradução
const getBrowserLang = () => {
  const lang = navigator.language || navigator.userLanguage || 'en';
  if (lang.startsWith('pt')) return 'pt-BR';
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('it')) return 'it';
  if (lang.startsWith('de')) return 'de';
  return 'en'; // Default to English if not supported
};

const [lang, setLang] = useState(getBrowserLang());
const t = translations[lang] || translations['en'];

// Opcional: permite ao usuário trocar o idioma manualmente
const handleLangChange = (e) => setLang(e.target.value);

  return (
    <div className="min-h-screen bg-[#f4f8fb] flex flex-col items-center py-8 px-2">
      <div className="w-full max-w-4xl mx-auto">
        {/* Idioma selector */}
        {/* Seletor removido, pois o idioma é detectado automaticamente */}
        {/* Header */}
        <div className="flex flex-col items-center mb-8 relative">
          {/* GitHub Icon Link */}
          <a
            href="https://github.com/myappstoolbox/xls_csv_to_ofx/blob/main/README.md"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute right-0 top-0 md:right-2 md:top-2"
            aria-label="GitHub Repository"
            title="Ver repositório no GitHub"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-7 h-7 text-[#3ec6e0] hover:text-[#222f3e] transition"
            >
              <path d="M12 2C6.477 2 2 6.484 2 12.021c0 4.428 2.865 8.184 6.839 9.504.5.092.682-.217.682-.483 0-.237-.009-.868-.014-1.703-2.782.605-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.004.07 1.532 1.032 1.532 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.339-2.221-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.987 1.029-2.686-.103-.254-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.025A9.564 9.564 0 0 1 12 6.844c.85.004 1.705.115 2.504.337 1.909-1.295 2.748-1.025 2.748-1.025.546 1.378.202 2.396.1 2.65.64.699 1.028 1.593 1.028 2.686 0 3.847-2.337 4.695-4.566 4.944.359.309.678.919.678 1.853 0 1.337-.012 2.419-.012 2.749 0 .268.18.579.688.481C19.138 20.2 22 16.447 22 12.021 22 6.484 17.523 2 12 2z" />
            </svg>
          </a>
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="16" rx="3" fill="#ffe082" stroke="#f9a825" strokeWidth="2"/>
              <path d="M7 8h10M7 12h6" stroke="#f9a825" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h1 className="text-3xl md:text-4xl font-bold text-[#3ec6e0] tracking-tight">{t.title}</h1>
          </div>
          <p className="text-[#7b8ca7] text-center text-base md:text-lg max-w-xl">
            {t.subtitle}
          </p>
        </div>
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#e6eaf0] p-6 md:p-10 w-full">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-[#222f3e] font-medium mb-2">{t.uploadLabel}</label>
            <div
              id="file-drop-area"
              className="border-2 border-dashed border-[#c8d6e5] rounded-xl bg-[#f8fafc] flex flex-col items-center justify-center py-8 px-4 text-[#8395a7] text-center cursor-pointer transition hover:border-[#3ec6e0] hover:bg-[#e6f7fa]"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                id="file-upload"
                type="file"
                accept=".txt,.csv,.xls,.xlsx"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
              />
              <svg className="w-10 h-10 mx-auto mb-2 text-[#3ec6e0]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 16V4M8 8l4-4 4 4" stroke="#3ec6e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="4" y="16" width="16" height="4" rx="2" fill="#e6f7fa" stroke="#3ec6e0" strokeWidth="2"/>
              </svg>
              <span className="block font-medium">{t.uploadHint}</span>
              <span className="block text-xs mt-1">{t.uploadMax}</span>
              <span className="block text-xs mt-1 text-[#7b8ca7]">{t.uploadInfo}</span>
            </div>
            {fileInfoMessage && <div className="mt-2 text-sm text-[#e17055]">{fileInfoMessage}</div>}
            {file && (
              <div className="mt-2 text-sm text-[#3ec6e0] font-medium truncate" title={file.name}>
                {t.selectedFile} <span className="font-semibold">{file.name}</span>
              </div>
            )}
          </div>
          {/* CSV Separator Selector - só para TXT/CSV */}
          {isTxtOrCsv(file) && (
            <div className="mb-4 flex flex-col gap-2">
              {detectedSeparator ? (
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <span className="text-[#8395a7] text-sm">
                    {t.separatorDetected} <span className="font-semibold text-[#3ec6e0]">{detectedSeparator}</span>.<br className="md:hidden" />
                    {' '}{t.separatorCheck} <span className="font-semibold">{t.separatorList}</span>.<br />
                  </span>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <span className="text-[#e17055] text-sm">
                    {t.separatorNotDetected}<br className="md:hidden" />
                    {t.separatorSave} <span className="font-semibold">{t.separatorList}</span>.<br />
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Has Headers Checkbox - só após selecionar arquivo */}
          {file && (
            <div className="mb-4 flex items-center gap-2">
              <input
                id="has-headers"
                type="checkbox"
                checked={hasHeaders}
                onChange={handleHasHeadersChange}
                className="rounded border-[#c8d6e5] text-[#3ec6e0] focus:ring-[#3ec6e0]"
              />
              <label htmlFor="has-headers" className="text-[#222f3e] text-sm">{t.hasHeaders}</label>
            </div>
          )}
          {/* Data Preview Table */}
          {parsedData.length > 0 && (
            <>
              <div className="mb-6 overflow-x-auto">
                <table className="min-w-full bg-[#f8fafc] rounded-xl overflow-hidden shadow border border-[#e6eaf0]">
                  <thead>
                    <tr>
                      {headers.map((h, idx) => (
                        <th key={idx} className="px-3 py-2 text-left text-xs font-semibold text-[#3ec6e0] bg-[#e6f7fa]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-b border-[#e6eaf0] last:border-0">
                        {headers.map((h, j) => {
                          const invalids = getInvalidCells();
                          let highlight = false;
                          if (h === mapping.date && invalids.date.has(i)) highlight = true;
                          if (h === mapping.amount && invalids.amount.has(i)) highlight = true;
                          if (h === mapping.creditColumn && invalids.creditColumn.has(i)) highlight = true;
                          if (h === mapping.debitColumn && invalids.debitColumn.has(i)) highlight = true;
                          if (h === mapping.balance && invalids.balance.has(i)) highlight = true;
                          return (
                            <td
                              key={j}
                              className={`px-3 py-2 text-sm ${highlight ? 'bg-[#ffeaea] text-[#e17055] font-bold' : 'text-[#222f3e]'}`}
                            >
                              {row[h]}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-xs text-[#8395a7] mt-1">{t.preview}</div>
              </div>
              {/* Formato de Valor: agora radio buttons, logo abaixo da pré-visualização */}
              <div className="mb-6 flex flex-col">
                <label className="block text-[#222f3e] font-medium mb-1">{t.valueFormat} <span className="text-[#e17055]">*</span></label>
                <div className="flex gap-4 mt-1">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="valueFormat"
                      value="single"
                      checked={valueFormat === 'single'}
                      onChange={handleValueFormatChange}
                      className="form-radio text-[#3ec6e0] focus:ring-[#3ec6e0]"
                    />
                    <span className="ml-2 text-[#222f3e]">{t.valueFormatSingle}</span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="valueFormat"
                      value="separate"
                      checked={valueFormat === 'separate'}
                      onChange={handleValueFormatChange}
                      className="form-radio text-[#3ec6e0] focus:ring-[#3ec6e0]"
                    />
                    <span className="ml-2 text-[#222f3e]">{t.valueFormatSeparate}</span>
                  </label>
                </div>
              </div>
            </>
          )}
          {/* Column Mapping */}
          {headers.length > 0 && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#222f3e] font-medium mb-1">{t.dateColumn} <span className="text-[#e17055]">*</span></label>
                <select
                  name="date"
                  value={mapping.date}
                  onChange={handleMappingChange}
                  className={`w-full rounded-lg border px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] ${errors.date ? 'border-[#e17055] bg-[#ffeaea]' : 'border-[#c8d6e5] bg-white'}`}
                >
                  <option value="">{t.select}</option>
                  {getAvailableHeaders('date').map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[#222f3e] font-medium mb-1">{t.descriptionColumn} <span className="text-[#e17055]">*</span></label>
                <select
                  name="description"
                  value={mapping.description}
                  onChange={handleMappingChange}
                  className={`w-full rounded-lg border px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] ${errors.description ? 'border-[#e17055] bg-[#ffeaea]' : 'border-[#c8d6e5] bg-white'}`}
                >
                  <option value="">{t.select}</option>
                  {getAvailableHeaders('description').map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>
              {/* ...existing code for amount/credit/debit/balance... */}
              {valueFormat === 'single' ? (
                <div>
                  <label className="block text-[#222f3e] font-medium mb-1">{t.amountColumn} <span className="text-[#e17055]">*</span></label>
                  <select
                    name="amount"
                    value={mapping.amount}
                    onChange={handleMappingChange}
                    className={`w-full rounded-lg border px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] ${errors.amount ? 'border-[#e17055] bg-[#ffeaea]' : 'border-[#c8d6e5] bg-white'}`}
                  >
                    <option value="">{t.select}</option>
                    {getAvailableHeaders('amount').map((h, i) => <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-[#222f3e] font-medium mb-1">{t.creditColumn} <span className="text-[#e17055]">*</span></label>
                    <select
                      name="creditColumn"
                      value={mapping.creditColumn}
                      onChange={handleMappingChange}
                      className={`w-full rounded-lg border px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] ${errors.creditColumn ? 'border-[#e17055] bg-[#ffeaea]' : 'border-[#c8d6e5] bg-white'}`}
                    >
                      <option value="">{t.select}</option>
                      {getAvailableHeaders('creditColumn').map((h, i) => <option key={i} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#222f3e] font-medium mb-1">{t.debitColumn} <span className="text-[#e17055]">*</span></label>
                    <select
                      name="debitColumn"
                      value={mapping.debitColumn}
                      onChange={handleMappingChange}
                      className={`w-full rounded-lg border px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] ${errors.debitColumn ? 'border-[#e17055] bg-[#ffeaea]' : 'border-[#c8d6e5] bg-white'}`}
                    >
                      <option value="">{t.select}</option>
                      {getAvailableHeaders('debitColumn').map((h, i) => <option key={i} value={h}>{h}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-[#222f3e] font-medium mb-1">{t.balanceColumn}</label>
                <select
                  name="balance"
                  value={mapping.balance}
                  onChange={handleMappingChange}
                  className="w-full rounded-lg border border-[#c8d6e5] px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] bg-white"
                >
                  <option value="">{t.select}</option>
                  {headers.filter(h => {
                    // Não permitir colunas já selecionadas em campos obrigatórios
                    const used = [mapping.date, mapping.description];
                    if (valueFormat === 'single') {
                      used.push(mapping.amount);
                    } else {
                      used.push(mapping.creditColumn, mapping.debitColumn);
                    }
                    // Permitir a coluna atualmente selecionada para saldo
                    return !used.includes(h) || mapping.balance === h;
                  }).map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          )}
          {/* Validation Warnings */}
          {validationWarnings.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-[#fff6e0] border border-[#ffe082] text-[#e17055] text-sm">
              <ul className="list-disc pl-5">
                {validationWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${message.includes('sucesso') ? 'bg-[#e6f7fa] text-[#3ec6e0]' : 'bg-[#ffeaea] text-[#e17055]'}`}>{message}</div>
          )}
          {/* Output File Name - só após gerar OFX */}
          {ofxContent && (
            <div className="mb-6">
              <label className="block text-[#222f3e] font-medium mb-1">{t.outputFile}</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={outputFileName.replace(/\.ofx$/i, '')}
                  onChange={e => {
                    // Permite editar apenas o nome, mantendo a extensão .ofx
                    let newName = e.target.value.replace(/\s+/g, '_').replace(/\.+$/, '');
                    if (!newName) newName = 'extrato';
                    setOutputFileName(newName + '.ofx');
                  }}
                  className="w-full rounded-lg border border-[#c8d6e5] px-3 py-2 text-[#222f3e] focus:outline-none focus:ring-2 focus:ring-[#3ec6e0] bg-white"
                />
                <span className="text-[#8395a7] font-mono text-sm">.ofx</span>
              </div>
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            {/* Botão Limpar só aparece se houver arquivo selecionado */}
            {file && (
              <button
                className="flex-1 py-3 rounded-lg bg-[#f1f2f6] text-[#8395a7] font-semibold hover:bg-[#e6eaf0] transition"
                type="button"
                onClick={() => window.location.reload()}
              >{t.clear}</button>
            )}
            {/* Botão Gerar OFX só aparece se houver arquivo selecionado E NÃO houver OFX gerado */}
            {file && !ofxContent && (
              <button
                className={`flex-1 py-3 rounded-lg font-semibold shadow-md transition text-white ${isMappingComplete() && parsedData.length > 0 && validationWarnings.length === 0 ? 'bg-[#3ec6e0] hover:bg-[#22b8cf]' : 'bg-[#b2bec3] cursor-not-allowed'}`}
                type="button"
                onClick={generateOFX}
                disabled={!isMappingComplete() || parsedData.length === 0 || validationWarnings.length > 0}
              >{t.generate}</button>
            )}
            {/* Botão de download só aparece se houver OFX gerado */}
            {ofxContent && (
              <button
                className="flex-1 py-3 rounded-lg font-semibold shadow-md transition text-white bg-[#21b573] hover:bg-[#16a34a]"
                type="button"
                onClick={downloadOFX}
              >{t.download}</button>
            )}
          </div>
          {/* OFX Preview (optional) */}
          {ofxContent && (
            <div className="mt-8">
              <label className="block text-[#222f3e] font-medium mb-2">{t.previewOfx}</label>
              <textarea
                className="w-full h-48 rounded-lg border border-[#c8d6e5] px-3 py-2 text-xs text-[#222f3e] bg-[#f8fafc] font-mono resize-none"
                value={ofxContent}
                readOnly
              />
            </div>
          )}
        </div>
        {/* Footer */}
        <footer className="mt-8 text-center text-[#b0b9c8] text-xs">
          <p>© {new Date().getFullYear()} <a href="https://myappstoolbox.com/" target="_blank" rel="noopener noreferrer" className="text-[#3ec6e0] hover:underline font-semibold">My Apps Toolbox</a>. {t.footer1}</p>
          <p>{t.footer2}</p>
          <p className="mt-2 text-[#7b8ca7]">
            {lang.startsWith('en') ? (
              <>
                <a href="https://github.com/myappstoolbox/xls_csv_to_ofx/blob/main/terms.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3ec6e0]">Terms of Use</a>
                {' | '}
                <a href="https://github.com/myappstoolbox/xls_csv_to_ofx/blob/main/privacy.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3ec6e0]">Privacy Policy</a>
              </>
            ) : (
              <>
                <a href="https://github.com/myappstoolbox/xls_csv_to_ofx/blob/main/terms.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3ec6e0]">Termos de Uso</a>
                {' | '}
                <a href="https://github.com/myappstoolbox/xls_csv_to_ofx/blob/main/privacy.md" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3ec6e0]">Política de Privacidade</a>
              </>
            )}
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;
